import { type MouseEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { inlineEditFailure, inlineEditStart, inlineEditStop, inlineEditSuccess } from 'reactApp/modules/modifying/modifying.actions';
import { getInlineEditState } from 'reactApp/modules/modifying/modifying.selectors';
import type { EInlineEditPlace } from 'reactApp/modules/modifying/modifying.types';
import { renameRequest } from 'reactApp/modules/modifying/sagas/rename.saga';
import { getItemById } from 'reactApp/modules/storage/storage.selectors';
import type { RootState } from 'reactApp/store';

export const useRenameInlineDoubleClick = (
    oldValue: string,
    id: string,
    place: EInlineEditPlace,
    ext?: string,
    isFolder?: boolean
): {
    isEdit: boolean;
    handleDoubleClick: (e: MouseEvent) => void;
    onRename: (value: { value: string; newExt: string }) => Promise<any>;
} => {
    const dispatch = useDispatch();
    const [isEdit, setIsEdit] = useState(false);
    const lastClickTime = useRef(0);
    const cloudItem = useSelector((state: RootState) => getItemById(state, id));
    // tempexp_14737-start
    const { inlineEditItemId, isInlineEditInProgress } = useSelector(getInlineEditState);

    useEffect(() => {
        if (isInlineEditInProgress && inlineEditItemId === id) {
            setIsEdit(true);
        }
    }, [id, inlineEditItemId, isInlineEditInProgress]);
    // tempexp_14737-end

    const handleDoubleClick = useCallback(() => {
        const now = Date.now();
        const timeSinceLastClick = now - lastClickTime.current;
        lastClickTime.current = now;

        if (timeSinceLastClick >= 300 && timeSinceLastClick <= 500) {
            setIsEdit(true);
            dispatch(inlineEditStart());
        }
    }, [setIsEdit, dispatch]);

    const onRename = useCallback(
        ({ value, newExt }: { value: string; newExt?: string }): Promise<any> => {
            const newName = value.trim().normalize();
            const oldName = oldValue.trim().normalize();
            const renamedExt = newExt?.trim().normalize();
            const oldExt = ext?.trim().normalize();

            if (newName !== oldName || (!!renamedExt && renamedExt !== oldExt)) {
                const finalName = cloudItem?.isFolder || (!renamedExt && !oldExt) ? newName : `${newName}.${renamedExt || oldExt}`;
                setIsEdit(false);

                return renameRequest(cloudItem, finalName)
                    .then((id) => {
                        dispatch(
                            inlineEditSuccess({
                                id,
                                place,
                                isFolder,
                            })
                        );
                    })
                    .catch((error) => {
                        dispatch(inlineEditFailure(error));
                    })
                    .finally(() => {
                        dispatch(inlineEditStop());
                    });
            }

            dispatch(inlineEditStop());
            setIsEdit(false);
            return Promise.resolve();
        },
        [cloudItem, dispatch, oldValue, place, ext]
    );

    return {
        isEdit,
        handleDoubleClick,
        onRename,
    };
};
